import React from "react";
import {
  Typography,
  Box,
  Button,
  Stack,
  CircularProgress,
  Alert,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import DownloadIndicator from "./DownloadIndicator";
import FolderIcon from "@diligentcorp/atlas-react-icons/dist/esm/lens/Folder.js";
import EmptyState from "./EmptyState";
import ArrowLeftIcon from "@diligentcorp/atlas-react-icons/dist/esm/lens/ArrowLeft.js";
import { downloadPolicyDocumentComplete } from "../services/policyDownloadService";
import type { PolicyDownloadRequest } from "../services/policyDownloadService";
import type { PolicyDocument } from "../types/api";

interface DocumentViewProps {
  selectedFolder: { key: string; value: string };
  documentsByType: PolicyDocument[];
  documentsByTypeLoading: boolean;
  documentsByTypeError: string | null;
  onBackToFolders: () => void;
}

const DocumentView: React.FC<DocumentViewProps> = ({
  selectedFolder,
  documentsByType,
  documentsByTypeLoading,
  documentsByTypeError,
  onBackToFolders,
}) => {
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down("sm"));
  const isSm = useMediaQuery(theme.breakpoints.between("sm", "md"));
  const isMd = useMediaQuery(theme.breakpoints.between("md", "lg"));

  // Refs to each letter section for smooth scrolling
  const sectionRefs = React.useRef<Record<string, HTMLDivElement | null>>({});

  // Responsive typography variants
  const getFolderTitleVariant = () => {
    if (isXs || isSm || isMd) return "h4";
    return "h2";
  };

  const getLetterHeadingVariant = () => {
    if (isXs || isSm || isMd) return "h5";
    return "h3";
  };

  const getAlphabetIndexVariant = () => {
    if (isXs || isSm || isMd) return "caption";
    return "body1";
  };

  const getDocumentVariant = () => {
    if (isXs || isSm || isMd) return "body2";
    return "body1";
  };

  // Get documents grouped by first letter
  const getDocumentsByLetter = () => {
    const grouped: { [key: string]: PolicyDocument[] } = {};

    documentsByType.forEach((doc) => {
      const firstChar = doc.name.charAt(0).toUpperCase();
      const isAlphabetic = /[A-Z]/.test(firstChar);
      const key = isAlphabetic ? firstChar : "#";

      if (!grouped[key]) {
        grouped[key] = [];
      }
      grouped[key].push(doc);
    });

    return grouped;
  };

  // Get alphabet letters and # that have documents
  const getAvailableLetters = () => {
    const letters = new Set<string>();

    documentsByType.forEach((doc) => {
      const firstChar = doc.name.charAt(0).toUpperCase();
      const isAlphabetic = /[A-Z]/.test(firstChar);
      letters.add(isAlphabetic ? firstChar : "#");
    });

    return Array.from(letters).sort((a, b) => {
      if (a === "#") return 1;
      if (b === "#") return -1;
      return a.localeCompare(b);
    });
  };

  // Datasets for rendering and lookups
  const documentsByLetter = getDocumentsByLetter();
  const availableLetters = getAvailableLetters();

  const getSectionKey = (letter: string) => (letter === "#" ? "num" : letter);

  const handleLetterClick = (letter: string) => {
    const target = sectionRefs.current[getSectionKey(letter)];
    if (target) {
      const rect = target.getBoundingClientRect();
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
      const isHeadingVisible = rect.top >= 0 && rect.top <= viewportHeight * 0.85;
      if (isHeadingVisible) return;
      target.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const [downloadingIds, setDownloadingIds] = React.useState<Set<string>>(new Set());

  // Create unique key for document tracking (id + language)
  const getDocumentKey = (document: PolicyDocument) => {
    return `${document.id}-${document.language || 'EN'}`;
  };

  // Handle document download
  const handleDocumentDownload = async (document: PolicyDocument) => {
    try {
      const documentKey = getDocumentKey(document);
      if (downloadingIds.has(documentKey)) return;
      setDownloadingIds((prev) => {
        const next = new Set(prev);
        next.add(documentKey);
        return next;
      });
      const downloadRequest: PolicyDownloadRequest = {
        agencyId: document.agency.id.toString(),
        platform_org_id: document.agency.platform_org_id,
        policyDocument: {
          id: document.id.toString(),
          name: document.name,
        },
        latestVersion: {
          versionNumber: document.version_number || "1.0",
        },
        languageCode: document.language || "EN",
      };

      await downloadPolicyDocumentComplete(downloadRequest);
    } catch (error) {
      console.error("Download failed:", error);
      // You might want to show a toast notification here
    } finally {
      const documentKey = getDocumentKey(document);
      setDownloadingIds((prev) => {
        const next = new Set(prev);
        next.delete(documentKey);
        return next;
      });
    }
  };

  return (
    <Box>
      {/* Header with Back Button and Folder Name */}
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
        <Button onClick={onBackToFolders}><ArrowLeftIcon size="xl" /></Button>
        <Stack direction="row" alignItems="center" spacing={1}>
          <FolderIcon
            size="lg"
            style={{ color: theme.palette.text.secondary }}
          />
          <Typography
            variant={getFolderTitleVariant()}
            sx={{
              fontWeight: 600,
              color: "text.primary",
            }}
          >
            {selectedFolder.value}
          </Typography>
        </Stack>
      </Stack>

      {/* Alphabet Index - Full Width */}
      <Box sx={{ mb: 2, width: "100%" }}>
        <Stack
          direction="row"
          spacing={0}
          sx={{
            width: "100%",
            justifyContent: "space-between",
            flexWrap: "nowrap",
            overflow: "hidden",
          }}
        >
          {["#", ..."ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("")].map((letter) => {
            const hasDocuments = availableLetters.includes(letter);
            return (
              <Typography
                key={letter}
                variant={getAlphabetIndexVariant()}
                sx={{
                  fontWeight: 600,
                  color: hasDocuments
                    ? "text.primary"
                    : "text.disabled",
                  cursor: hasDocuments ? "pointer" : "default",
                  textAlign: "center",
                  flex: "1 1 0",
                  minWidth: 0,
                  textDecoration: "none",
                  // Responsive padding
                  px: {
                    xs: 0.25, // 2px on extra small screens
                    sm: 0.5,  // 4px on small screens
                    md: 0.75, // 6px on medium screens
                    lg: 1,    // 8px on large screens
                    xl: 1.5,  // 12px on extra large screens (per Figma)
                  },
                  py: 1,
                  // Responsive font size override for better control
                  fontSize: {
                    xs: "0.625rem",  // 10px on extra small
                    sm: "0.75rem",   // 12px on small
                    md: "0.875rem",  // 14px on medium
                    lg: "1rem",      // 16px on large+ (per Figma)
                  },
                  "&:hover": hasDocuments
                    ? {
                        textDecoration: "underline",
                      }
                    : {},
                }}
                onClick={hasDocuments ? () => handleLetterClick(letter) : undefined}
                role={hasDocuments ? "button" : undefined}
                tabIndex={hasDocuments ? 0 : -1}
              >
                {letter}
              </Typography>
            );
          })}
        </Stack>
      </Box>

      {/* Documents grouped by letter - Vertical alignment */}
      {documentsByTypeLoading ? (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          height="200px"
        >
          <CircularProgress />
        </Box>
      ) : documentsByTypeError ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {documentsByTypeError}
        </Alert>
      ) : documentsByType.length === 0 ? (
        <EmptyState
          title="No items to display."
          subtitle="No documents in this folder"
        />
      ) : (
        <Box>
          {Object.entries(documentsByLetter)
            .sort(([a], [b]) => {
              if (a === "#") return 1;
              if (b === "#") return -1;
              return a.localeCompare(b);
            })
            .map(([letter, documents]) => (
              <Box
                key={letter}
                id={getSectionKey(letter)}
                ref={(el: HTMLDivElement | null) => {
                  sectionRefs.current[getSectionKey(letter)] = el;
                }}
                sx={{ mb: 2, scrollMarginTop: theme.spacing(8) }}
              >
                <Typography
                  variant={getLetterHeadingVariant()}
                  sx={{
                    mb: 2,
                    fontWeight: 600,
                    color: "text.primary",
                  }}
                >
                  {letter}
                </Typography>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  {documents.map((document, index) => {
                    const documentKey = getDocumentKey(document);
                    const isDownloading = downloadingIds.has(documentKey);
                    return (
                      <Stack key={`${document.id}-${document.language || 'EN'}-${index}`} direction="row" alignItems="center" spacing={1}>
                        <Typography
                          variant={getDocumentVariant()}
                          onClick={() => handleDocumentDownload(document)}
                          sx={{
                            fontWeight: 600,
                            cursor: isDownloading ? "default" : "pointer",
                            pointerEvents: isDownloading ? "none" : "auto",
                            opacity: isDownloading ? 0.6 : 1,
                            color: "text.primary",
                            textDecoration: "underline",
                            textDecorationSkipInk: "none",
                            textUnderlinePosition: "from-font",
                            "&:hover": {
                              textDecoration: "underline",
                            },
                          }}
                        >
                          {document.name}
                        </Typography>
                        {isDownloading && (
                          <DownloadIndicator size={16} />
                        )}
                      </Stack>
                    );
                  })}
                </Box>
              </Box>
            ))}
        </Box>
      )}
    </Box>
  );
};

export default DocumentView;
